import dayjs from "dayjs";

const DEFAULT_THRESHOLDS = {
  good: 70,
  warning: 45,
  risk: 50,
  critical: 35,
};

const average = (numbers) => {
  if (!numbers.length) {
    return 0;
  }

  return numbers.reduce((sum, value) => sum + value, 0) / numbers.length;
};

const standardDeviation = (numbers) => {
  if (numbers.length < 2) {
    return 0;
  }

  const avg = average(numbers);
  const variance = average(numbers.map((value) => (value - avg) ** 2));
  return Math.sqrt(variance);
};

const mergeIntervals = (intervals) => {
  if (!intervals.length) {
    return [];
  }

  const sorted = intervals
    .map((interval) => ({ ...interval }))
    .sort((a, b) => a.startAt - b.startAt);

  const merged = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i];
    const last = merged[merged.length - 1];

    if (current.startAt <= last.endAt) {
      last.endAt = Math.max(last.endAt, current.endAt);
      continue;
    }

    merged.push(current);
  }

  return merged;
};

const getIntervalsDuration = (intervals) =>
  mergeIntervals(intervals).reduce(
    (sum, interval) => sum + (interval.endAt - interval.startAt),
    0
  );

const getMessageCount = (event) => {
  if (event.messageCount !== undefined && event.messageCount !== null) {
    const count = Number(event.messageCount);

    return Number.isFinite(count) ? Math.max(0, count) : 1;
  }

  return 1;
};

const getOverlap = (startA, endA, startB, endB) =>
  Math.max(0, Math.min(endA, endB) - Math.max(startA, startB));

const getRiskColor = (activityPercent, thresholds) => {
  if (activityPercent >= thresholds.good) {
    return "green";
  }

  if (activityPercent >= thresholds.warning) {
    return "yellow";
  }

  return "red";
};

const getActivityBands = (startAt, endAt) => {
  const visibleDuration = Math.max(endAt - startAt, 0);
  const firstBreak = startAt + visibleDuration / 3;
  const secondBreak = startAt + (visibleDuration / 3) * 2;

  return {
    morning: { startAt, endAt: firstBreak },
    day: { startAt: firstBreak, endAt: secondBreak },
    evening: { startAt: secondBreak, endAt },
  };
};

const createEmptyDistribution = () => ({
  morning: 0,
  day: 0,
  evening: 0,
});

const trimEvents = (events, startAt, endAt) =>
  events
    .map((event) => {
      const eventStart = Math.max(event.startAt, startAt);
      const eventEnd = Math.min(event.startAt + event.duration, endAt);
      const duration = Math.max(eventEnd - eventStart, 0);

      return {
        ...event,
        startAt: eventStart,
        endAt: eventStart + duration,
        duration,
      };
    })
    .filter((event) => event.duration > 0);

const summarizeDay = ({
  day,
  userId,
  dailyDataMap,
  includeWeekends,
  thresholds,
  getEventGroup,
  getNowEndForWorkTime,
  messageTypeIds,
  messageDailyDataMap,
  minCallDurationSec,
}) => {
  const dayData = dailyDataMap[day];
  const messageDayData = messageDailyDataMap ? messageDailyDataMap[day] : null;
  const workTime = dayData && dayData.workTime ? dayData.workTime[userId] : null;
  const isWeekend = [0, 6].includes(dayjs(day).day());
  const isExcluded = !includeWeekends && isWeekend;

  if (!workTime) {
    return {
      day,
      userId,
      isWeekend,
      isExcluded,
      isNoData: true,
      hasActions: false,
      visibleDuration: 0,
      activeDuration: 0,
      crmDuration: 0,
      idleDuration: 0,
      callsDuration: 0,
      callCount: 0,
      messageCount: 0,
      activityPercent: 0,
      distribution: createEmptyDistribution(),
      color: "gray",
      risk: false,
      reason: "нет данных",
    };
  }

  const endAt = getNowEndForWorkTime(day, workTime);
  const visibleDuration = Math.max(endAt - workTime.fromTimeUnix, 0);
  const events = trimEvents(
    (dayData.events && dayData.events[userId]) || [],
    workTime.fromTimeUnix,
    endAt
  ).map((event) => ({
    ...event,
    group: getEventGroup(event),
  }));
  const messageEvents = messageDayData
    ? trimEvents(
        (messageDayData.events && messageDayData.events[userId]) || [],
        workTime.fromTimeUnix,
        endAt
      )
    : events.filter((event) => messageTypeIds.includes(Number(event.typeId)));
  const messageTimeSet = new Set(
    messageEvents.map((event) => event.originalStartAt || event.messageAt || event.startAt)
  );
  const nonMessageEvents = events.filter((event) => {
    if (Number(event.typeId) !== 0 || !messageTimeSet.size) {
      return true;
    }

    return !messageTimeSet.has(
      event.originalStartAt || event.messageAt || event.startAt
    );
  });
  const activeIntervals = nonMessageEvents.map((event) => ({
    startAt: event.startAt,
    endAt: event.endAt,
  }));
  const crmEvents = nonMessageEvents.filter(
    (event) => event.group === "simple_active"
  );
  const crmIntervals = crmEvents.map((event) => ({
    startAt: event.startAt,
    endAt: event.endAt,
  }));
  const filteredCallEvents = events.filter(
    (event) => event.group === "calls" && event.duration >= minCallDurationSec
  );
  const callIntervals = filteredCallEvents.map((event) => ({
    startAt: event.startAt,
    endAt: event.endAt,
  }));
  const messageIntervals = messageEvents.map((event) => ({
    startAt: event.startAt,
    endAt: event.endAt,
  }));
  const activeDuration = getIntervalsDuration([
    ...activeIntervals,
    ...messageIntervals,
  ]);
  const crmDuration = getIntervalsDuration(crmIntervals);
  const callsDuration = getIntervalsDuration(callIntervals);
  const callCount = filteredCallEvents.length;
  const messageCount = messageEvents.reduce(
    (sum, event) => sum + getMessageCount(event),
    0
  );
  const idleDuration = Math.max(visibleDuration - activeDuration, 0);
  const activityPercent = visibleDuration
    ? Math.round((activeDuration / visibleDuration) * 100)
    : 0;
  const bands = getActivityBands(workTime.fromTimeUnix, endAt);
  const distribution = Object.entries(bands).reduce(
    (result, [key, band]) => ({
      ...result,
      [key]: activeIntervals.reduce(
        (sum, interval) =>
          sum + getOverlap(interval.startAt, interval.endAt, band.startAt, band.endAt),
        0
      ),
    }),
    createEmptyDistribution()
  );

  return {
    day,
    userId,
    isWeekend,
    isExcluded,
    isNoData: false,
    hasActions: events.length > 0,
    visibleDuration,
    activeDuration,
    crmDuration,
    idleDuration,
    callsDuration,
    callCount,
    messageCount,
    activityPercent,
    distribution,
    color: isExcluded ? "gray" : getRiskColor(activityPercent, thresholds),
    risk: !isExcluded && activityPercent < thresholds.risk,
    reason:
      events.length === 0
        ? "нет действий"
        : activityPercent < thresholds.warning
          ? "низкая активность"
          : idleDuration > activeDuration
            ? "простой"
            : "нестабильный день",
  };
};

const buildUserAggregate = (user, daysSummary, previousAverage, thresholds) => {
  const includedDays = daysSummary.filter((day) => !day.isExcluded && !day.isNoData);
  const activityValues = includedDays.map((day) => day.activityPercent);
  const riskDays = includedDays.filter((day) => day.risk);
  const averageActivity = Math.round(average(activityValues));
  const averageActiveDuration = Math.round(
    average(includedDays.map((day) => day.activeDuration))
  );
  const averageCrmDuration = Math.round(
    average(includedDays.map((day) => day.crmDuration))
  );
  const averageIdleDuration = Math.round(
    average(includedDays.map((day) => day.idleDuration))
  );
  const averageCallsDuration = Math.round(
    average(includedDays.map((day) => day.callsDuration))
  );
  const totalCallCount = includedDays.reduce(
    (sum, day) => sum + day.callCount,
    0
  );
  const totalMessageCount = includedDays.reduce(
    (sum, day) => sum + day.messageCount,
    0
  );
  const totals = includedDays.reduce(
    (acc, day) => {
      acc.morning += day.distribution.morning;
      acc.day += day.distribution.day;
      acc.evening += day.distribution.evening;
      return acc;
    },
    createEmptyDistribution()
  );
  const totalDistribution = totals.morning + totals.day + totals.evening || 1;
  const stability = Math.round(
    100 - Math.min(standardDeviation(activityValues) * 2, 100)
  );
  let status = "normal";

  if (averageActivity < thresholds.warning) {
    status = "critical";
  } else if (averageActivity < thresholds.good) {
    status = "warning";
  }

  return {
    id: user.id,
    name: user.name,
    departmentId: user.departmentId,
    departmentName: user.departmentName,
    days: daysSummary,
    averageActivity,
    averageActiveDuration,
    averageCrmDuration,
    averageIdleDuration,
    averageCallsDuration,
    totalCallCount,
    totalMessageCount,
    riskDaysCount: riskDays.length,
    trend: averageActivity - Math.round(previousAverage || 0),
    previousAverage: Math.round(previousAverage || 0),
    stability,
    status,
    distributionPercent: {
      morning: Math.round((totals.morning / totalDistribution) * 100),
      day: Math.round((totals.day / totalDistribution) * 100),
      evening: Math.round((totals.evening / totalDistribution) * 100),
    },
    worstDays: includedDays
      .slice()
      .sort((a, b) => a.activityPercent - b.activityPercent)
      .slice(0, 5),
  };
};

export const buildPeriodAnalytics = ({
  currentDays,
  previousDays,
  periodDailyData,
  comparisonDailyData,
  periodMessageDailyData = [],
  users,
  visibleUserIds,
  includeWeekends,
  getEventGroup,
  getNowEndForWorkTime,
  thresholds = DEFAULT_THRESHOLDS,
  messageTypeIds = [],
  minCallDurationSec = 0,
}) => {
  const currentDataMap = Object.fromEntries(
    periodDailyData.map((item) => [item.day, item])
  );
  const previousDataMap = Object.fromEntries(
    comparisonDailyData.map((item) => [item.day, item])
  );
  const messageDataMap = Object.fromEntries(
    periodMessageDailyData.map((item) => [item.day, item])
  );
  const targetUsers = users.filter((user) => visibleUserIds.includes(user.id));
  const previousAveragesByUser = {};

  for (const user of targetUsers) {
    const previousSummaries = previousDays.map((day) =>
      summarizeDay({
        day,
        userId: user.id,
        dailyDataMap: previousDataMap,
        includeWeekends,
        thresholds,
        getEventGroup,
        getNowEndForWorkTime,
        messageTypeIds,
        messageDailyDataMap: null,
        minCallDurationSec,
      })
    );

    const included = previousSummaries.filter(
      (summary) => !summary.isExcluded && !summary.isNoData
    );
    previousAveragesByUser[user.id] = average(
      included.map((summary) => summary.activityPercent)
    );
  }

  const userRows = targetUsers.map((user) => {
    const daysSummary = currentDays.map((day) =>
      summarizeDay({
        day,
        userId: user.id,
        dailyDataMap: currentDataMap,
        includeWeekends,
        thresholds,
        getEventGroup,
        getNowEndForWorkTime,
        messageTypeIds,
        messageDailyDataMap: messageDataMap,
        minCallDurationSec,
      })
    );

    return buildUserAggregate(
      user,
      daysSummary,
      previousAveragesByUser[user.id],
      thresholds
    );
  });

  const allIncludedDays = userRows.flatMap((user) =>
    user.days.filter((day) => !day.isExcluded && !day.isNoData)
  );

  const teamSummary = {
    averageActivity: Math.round(
      average(allIncludedDays.map((day) => day.activityPercent))
    ),
    averageActiveDuration: Math.round(
      average(allIncludedDays.map((day) => day.activeDuration))
    ),
    averageCrmDuration: Math.round(
      average(allIncludedDays.map((day) => day.crmDuration))
    ),
    averageCallsDuration: Math.round(
      average(allIncludedDays.map((day) => day.callsDuration))
    ),
    totalCallCount: allIncludedDays.reduce(
      (sum, day) => sum + day.callCount,
      0
    ),
    totalMessageCount: allIncludedDays.reduce(
      (sum, day) => sum + day.messageCount,
      0
    ),
    normalCount: userRows.filter((user) => user.status === "normal").length,
    warningCount: userRows.filter((user) => user.status === "warning").length,
    criticalCount: userRows.filter((user) => user.status === "critical").length,
    riskDaysCount: userRows.reduce((sum, user) => sum + user.riskDaysCount, 0),
  };

  const ranking = userRows
    .slice()
    .sort((a, b) => {
      if (b.averageActivity !== a.averageActivity) {
        return b.averageActivity - a.averageActivity;
      }

      if (b.averageCrmDuration !== a.averageCrmDuration) {
        return b.averageCrmDuration - a.averageCrmDuration;
      }

      return a.averageIdleDuration - b.averageIdleDuration;
    });

  const teamTrend = currentDays.map((day) => {
    const cells = userRows
      .map((user) => user.days.find((entry) => entry.day === day))
      .filter((entry) => entry && !entry.isExcluded && !entry.isNoData);

    return {
      day,
      activityPercent: Math.round(
        average(cells.map((cell) => cell.activityPercent))
      ),
      activeDuration: Math.round(
        average(cells.map((cell) => cell.activeDuration))
      ),
      employeeCount: cells.length,
    };
  });

  const insights = [];
  const riskThreshold = Math.max(3, Math.ceil(currentDays.length * 0.25));

  ranking.forEach((user) => {
    const includedDays = user.days.filter((day) => !day.isExcluded && !day.isNoData);

    if (user.riskDaysCount >= riskThreshold) {
      insights.push({
        id: `risk-${user.id}`,
        title: `${user.name} — ${user.riskDaysCount} дней риска за период`,
        type: "user-risk",
        userIds: [user.id],
        days: user.days.filter((day) => day.risk).map((day) => day.day),
      });
    }

    const morningAverage = average(
      includedDays.map((day) =>
        day.visibleDuration
          ? Math.round((day.distribution.morning / day.visibleDuration) * 100)
          : 0
      )
    );
    const dayAverage = average(
      includedDays.map((day) =>
        day.visibleDuration
          ? Math.round((day.distribution.day / day.visibleDuration) * 100)
          : 0
      )
    );

    if (morningAverage - dayAverage >= 15 && includedDays.length >= 4) {
      insights.push({
        id: `after-lunch-${user.id}`,
        title: `${user.name} системно проседает после обеда`,
        type: "after-lunch",
        userIds: [user.id],
        days: includedDays
          .filter((day) => day.activityPercent < thresholds.good)
          .map((day) => day.day),
      });
    }

    if (standardDeviation(includedDays.map((day) => day.activityPercent)) >= 18) {
      insights.push({
        id: `unstable-${user.id}`,
        title: `${user.name} работает нестабильно`,
        type: "unstable",
        userIds: [user.id],
        days: includedDays.map((day) => day.day),
      });
    }
  });

  return {
    teamSummary,
    ranking,
    teamTrend,
    insights: insights.slice(0, 8),
    userRows,
    thresholds,
  };
};
